import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
}

/**
 * Converts a hex color to a pastel version by increasing lightness and reducing saturation
 */
export function pastelizeHex(hex: string): string {
  // Remove # if present
  const cleanHex = hex.replace('#', '');
  
  // Convert hex to RGB
  const r = parseInt(cleanHex.substr(0, 2), 16);
  const g = parseInt(cleanHex.substr(2, 2), 16);
  const b = parseInt(cleanHex.substr(4, 2), 16);
  
  // Convert RGB to HSL
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;
  
  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  let h, s, l;
  
  l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case rNorm: h = (gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0); break;
      case gNorm: h = (bNorm - rNorm) / d + 2; break;
      case bNorm: h = (rNorm - gNorm) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }
  
  // Make it pastel: reduce saturation to 40% and increase lightness to 75%
  const pastelS = Math.min(s * 0.4, 0.4);
  const pastelL = Math.max(l, 0.75);
  
  // Convert back to RGB
  function hue2rgb(p: number, q: number, t: number) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  }
  
  let rFinal, gFinal, bFinal;
  
  if (pastelS === 0) {
    rFinal = gFinal = bFinal = pastelL; // achromatic
  } else {
    const q = pastelL < 0.5 ? pastelL * (1 + pastelS) : pastelL + pastelS - pastelL * pastelS;
    const p = 2 * pastelL - q;
    rFinal = hue2rgb(p, q, h + 1/3);
    gFinal = hue2rgb(p, q, h);
    bFinal = hue2rgb(p, q, h - 1/3);
  }
  
  // Convert back to hex
  const rHex = Math.round(rFinal * 255).toString(16).padStart(2, '0');
  const gHex = Math.round(gFinal * 255).toString(16).padStart(2, '0');
  const bHex = Math.round(bFinal * 255).toString(16).padStart(2, '0');
  
  return `#${rHex}${gHex}${bHex}`;
}

/**
 * Determines if a hex color is considered "light" based on relative luminance.
 * Returns true if the color is light (should use dark text), false if dark (should use light text).
 */
export function isLightColor(hex: string | undefined | null): boolean {
  // Handle undefined/null - default to assuming dark background
  if (!hex) return false;
  
  // Remove # if present
  const cleanHex = hex.replace('#', '');
  
  // Handle short hex notation
  let fullHex = cleanHex;
  if (cleanHex.length === 3) {
    fullHex = cleanHex.split('').map(c => c + c).join('');
  }
  
  // Convert hex to RGB
  const r = parseInt(fullHex.substr(0, 2), 16);
  const g = parseInt(fullHex.substr(2, 2), 16);
  const b = parseInt(fullHex.substr(4, 2), 16);
  
  // Calculate relative luminance using the sRGB formula
  // See: https://www.w3.org/TR/WCAG20/#relativeluminancedef
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Threshold of 0.5 is a good balance for most colors
  return luminance > 0.5;
}

/**
 * Returns 'black' or 'white' based on background color for optimal contrast.
 */
export function getContrastTextColor(hex: string | undefined | null): 'black' | 'white' {
  return isLightColor(hex) ? 'black' : 'white';
}
